import { useMemo, useCallback, useRef, useEffect } from 'react'
import { Canvas, useFrame, ThreeEvent, useThree } from '@react-three/fiber'
import { OrbitControls, Line } from '@react-three/drei'
import * as THREE from 'three'
import { useSceneStore, useAnimationStore, useUIStore, useOptimizerStore, OPTIMIZER_COLORS } from '../../stores'
import { generateManifoldLandscape, LandscapeData } from '../../utils/landscapes'
import styles from './Canvas3D.module.css'

// Height scale factor for the landscape
const HEIGHT_SCALE = 2.0

interface LandscapeMeshProps {
  data: LandscapeData
  showWireframe?: boolean
  heightScale?: number
}

function LandscapeMesh({ data, meshRef, showWireframe = true, heightScale = HEIGHT_SCALE }: LandscapeMeshProps & { meshRef?: React.RefObject<THREE.Mesh> }) {
  const theme = useUIStore(state => state.theme)
  const pickingMode = useUIStore(state => state.pickingMode)
  const setPickingMode = useUIStore(state => state.setPickingMode)
  const setStartPosition = useSceneStore(state => state.setStartPosition)
  const manifoldRange = useSceneStore(state => state.manifoldRange)
  const animationState = useAnimationStore(state => state.state)
  
  const handleMeshClick = useCallback((event: ThreeEvent<MouseEvent>) => {
    if (!pickingMode || animationState === 'playing') return
    
    event.stopPropagation()
    
    // Get the intersection point in world coordinates
    const point = event.point
    const worldX = point.x
    const worldZ = point.z
    
    // Convert world coordinates back to parameter space
    // Inverse of: worldX = ((paramX - rangeMin) / rangeSize - 0.5) * 10
    //             worldZ = -((paramY - rangeMin) / rangeSize - 0.5) * 10
    const [rangeMin, rangeMax] = manifoldRange
    const rangeSize = rangeMax - rangeMin
    
    const paramX = ((worldX / 10) + 0.5) * rangeSize + rangeMin
    const paramY = ((-worldZ / 10) + 0.5) * rangeSize + rangeMin
    
    // Clamp to manifold range
    const clampedX = Math.max(rangeMin, Math.min(rangeMax, paramX))
    const clampedY = Math.max(rangeMin, Math.min(rangeMax, paramY))
    
    // Update start position
    setStartPosition(clampedX, clampedY)
    
    // Exit picking mode
    setPickingMode(false)
  }, [pickingMode, animationState, manifoldRange, setStartPosition, setPickingMode])
  
  const geometry = useMemo(() => {
    const rows = data.z.length
    const cols = data.z[0].length
    
    const geo = new THREE.PlaneGeometry(10, 10, cols - 1, rows - 1)
    const positions = geo.attributes.position
    
    // Find z range
    const zFlat = data.z.flat()
    const zMin = Math.min(...zFlat)
    const zMax = Math.max(...zFlat)
    const zRangeVal = zMax - zMin
    
    // Update vertex positions
    for (let i = 0; i < positions.count; i++) {
      const row = Math.floor(i / cols)
      const col = i % cols
      
      if (row < rows && col < cols) {
        const normalizedZ = (data.z[row][col] - zMin) / zRangeVal
        positions.setZ(i, normalizedZ * heightScale)
        
        const xPos = (col / (cols - 1) - 0.5) * 10
        const yPos = (row / (rows - 1) - 0.5) * 10
        positions.setX(i, xPos)
        positions.setY(i, yPos)
      }
    }
    
    positions.needsUpdate = true
    geo.computeVertexNormals()
    
    // Create vertex colors
    const colorArray: number[] = []
    for (let i = 0; i < positions.count; i++) {
      const z = positions.getZ(i)
      const normalizedZ = z / heightScale
      
      let r: number, g: number, b: number
      if (normalizedZ < 0.25) {
        r = 0; g = normalizedZ * 4; b = 1
      } else if (normalizedZ < 0.5) {
        r = 0; g = 1; b = 1 - (normalizedZ - 0.25) * 4
      } else if (normalizedZ < 0.75) {
        r = (normalizedZ - 0.5) * 4; g = 1; b = 0
      } else {
        r = 1; g = 1 - (normalizedZ - 0.75) * 4; b = 0
      }
      
      colorArray.push(r, g, b)
    }
    
    geo.setAttribute('color', new THREE.Float32BufferAttribute(colorArray, 3))
    
    return geo
  }, [data, heightScale])
  
  const wireframeColor = theme === 'dark' ? '#444444' : '#999999'
  
  return (
    <>
      <mesh 
        ref={meshRef}
        geometry={geometry} 
        rotation={[-Math.PI / 2, 0, 0]}
        onClick={handleMeshClick}
      >
        <meshPhongMaterial vertexColors side={THREE.DoubleSide} flatShading={false} />
      </mesh>
      {showWireframe && (
        <mesh geometry={geometry.clone()} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
          <meshBasicMaterial color={wireframeColor} wireframe transparent opacity={0.15} depthWrite={false} />
        </mesh>
      )}
    </>
  )
}

interface StartPointMarkerProps {
  x: number
  y: number
  landscapeData: LandscapeData | null
  heightScale?: number
}

function StartPointMarker({ x, y, landscapeData, heightScale = HEIGHT_SCALE }: StartPointMarkerProps) {
  if (!landscapeData) return null
  
  const [rangeMin, rangeMax] = landscapeData.x_range
  const rangeSize = rangeMax - rangeMin
  
  // Map parameter space to world space
  const worldX = ((x - rangeMin) / rangeSize - 0.5) * 10
  const worldZ = -((y - rangeMin) / rangeSize - 0.5) * 10
  
  // Calculate height at this position
  const normalizedX = (x - rangeMin) / rangeSize
  const normalizedY = (y - rangeMin) / rangeSize
  
  const resolution = landscapeData.z.length
  const col = Math.min(Math.floor(normalizedX * (resolution - 1)), resolution - 2)
  const row = Math.min(Math.floor(normalizedY * (resolution - 1)), resolution - 2)
  
  const zFlat = landscapeData.z.flat()
  const zMin = Math.min(...zFlat)
  const zMax = Math.max(...zFlat)
  const zRange = zMax - zMin
  
  const z00 = (landscapeData.z[row][col] - zMin) / zRange * heightScale
  const z01 = (landscapeData.z[row][Math.min(col + 1, resolution - 1)] - zMin) / zRange * heightScale
  const z10 = (landscapeData.z[Math.min(row + 1, resolution - 1)][col] - zMin) / zRange * heightScale
  const z11 = (landscapeData.z[Math.min(row + 1, resolution - 1)][Math.min(col + 1, resolution - 1)] - zMin) / zRange * heightScale
  
  const fx = normalizedX * (resolution - 1) - col
  const fy = normalizedY * (resolution - 1) - row
  
  const h0 = z00 * (1 - fx) + z01 * fx
  const h1 = z10 * (1 - fx) + z11 * fx
  const worldY = h0 * (1 - fy) + h1 * fy
  
  const points: [number, number, number][] = [
    [worldX, worldY, worldZ],
    [worldX, worldY + 1.5, worldZ],
  ]
  
  return (
    <Line
      points={points}
      color="#ffaa00"
      lineWidth={3}
      transparent
      opacity={0.9}
    />
  )
}

interface TrajectoryLinesProps {
  trajectories: Record<string, Array<{ x: number; y: number; z: number }>>
  landscapeData: LandscapeData | null
  currentStep: number
  showTrails: boolean
  heightScale?: number
}

function TrajectoryLines({ trajectories, landscapeData, currentStep, showTrails, heightScale = HEIGHT_SCALE }: TrajectoryLinesProps) {
  if (!landscapeData || !showTrails || !trajectories || Object.keys(trajectories).length === 0) return null
  
  const [rangeMin, rangeMax] = landscapeData.x_range
  const rangeSize = rangeMax - rangeMin
  
  const zFlat = landscapeData.z.flat()
  const zMin = Math.min(...zFlat)
  const zMax = Math.max(...zFlat)
  const zRange = zMax - zMin || 1 // Prevent division by zero
  
  return (
    <>
      {Object.entries(trajectories).map(([optimizer, points]) => {
        if (!points || points.length === 0) return null
        
        // Show at least up to currentStep + 1, minimum 2 points for a line
        const endIndex = Math.max(2, currentStep + 1)
        const visiblePoints = points.slice(0, Math.min(endIndex, points.length))
        if (visiblePoints.length < 2) return null
        
        const linePoints: [number, number, number][] = visiblePoints.map(p => {
          const worldX = ((p.x - rangeMin) / rangeSize - 0.5) * 10
          const worldZ = -((p.y - rangeMin) / rangeSize - 0.5) * 10
          const worldY = ((p.z - zMin) / zRange) * heightScale + 0.05
          return [worldX, worldY, worldZ]
        })
        
        const color = OPTIMIZER_COLORS[optimizer] || '#ffffff'
        
        return (
          <Line
            key={optimizer}
            points={linePoints}
            color={color}
            lineWidth={2}
          />
        )
      })}
    </>
  )
}

interface OptimizerBallsProps {
  trajectories: Record<string, Array<{ x: number; y: number; z: number }>>
  landscapeData: LandscapeData | null
  currentStep: number
  heightScale?: number
}

function OptimizerBalls({ trajectories, landscapeData, currentStep, heightScale = HEIGHT_SCALE }: OptimizerBallsProps) {
  const enabled = useOptimizerStore(state => state.enabled)
  
  if (!landscapeData || !trajectories || Object.keys(trajectories).length === 0) return null
  
  const [rangeMin, rangeMax] = landscapeData.x_range
  const rangeSize = rangeMax - rangeMin
  
  const zFlat = landscapeData.z.flat()
  const zMin = Math.min(...zFlat)
  const zMax = Math.max(...zFlat)
  const zRange = zMax - zMin
  
  return (
    <>
      {Object.entries(trajectories).map(([optimizer, points]) => {
        if (!points || points.length === 0 || !enabled[optimizer as keyof typeof enabled]) return null
        
        const pointIndex = Math.min(currentStep, points.length - 1)
        const p = points[pointIndex]
        
        const worldX = ((p.x - rangeMin) / rangeSize - 0.5) * 10
        const worldZ = -((p.y - rangeMin) / rangeSize - 0.5) * 10
        const worldY = ((p.z - zMin) / zRange) * heightScale + 0.1
        
        const color = OPTIMIZER_COLORS[optimizer] || '#ffffff'
        
        return (
          <mesh key={optimizer} position={[worldX, worldY, worldZ]}>
            <sphereGeometry args={[0.15, 16, 16]} />
            <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.3} />
          </mesh>
        )
      })}
    </>
  )
}

// Animation controller component that runs inside the Canvas
function AnimationController() {
  const animationState = useAnimationStore(state => state.state)
  const currentStep = useAnimationStore(state => state.currentStep)
  const totalSteps = useAnimationStore(state => state.totalSteps)
  const speed = useAnimationStore(state => state.speed)
  const setCurrentStep = useAnimationStore(state => state.setCurrentStep)
  const pause = useAnimationStore(state => state.pause)
  
  const accumulatorRef = useRef(0)
  
  useFrame((_, delta) => {
    if (animationState !== 'playing') {
      accumulatorRef.current = 0
      return
    }
    
    // Accumulate time, advance step based on speed
    // Base rate: ~100 steps per second at speed 1.0 (fast enough for 10k+ step trajectories)
    accumulatorRef.current += delta * speed * 100
    
    if (accumulatorRef.current >= 1) {
      const stepsToAdvance = Math.floor(accumulatorRef.current)
      accumulatorRef.current -= stepsToAdvance
      
      const newStep = currentStep + stepsToAdvance
      if (newStep >= totalSteps) {
        setCurrentStep(totalSteps)
        pause()
      } else {
        setCurrentStep(newStep)
      }
    }
  })
  
  return null
}

// Keyboard controls component for WASD camera movement
interface KeyboardControlsProps {
  orbitControlsRef: React.RefObject<any>
}

function KeyboardControls({ orbitControlsRef }: KeyboardControlsProps) {
  const { camera } = useThree()
  const keysPressed = useRef(new Set<string>())
  
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase()
      
      // Log camera view parameters when 'V' is pressed
      if (key === 'v' && orbitControlsRef.current) {
        event.preventDefault()
        const fov = (camera as THREE.PerspectiveCamera).fov ?? 60
        const cameraData = {
          position: {
            x: camera.position.x,
            y: camera.position.y,
            z: camera.position.z,
          },
          target: {
            x: orbitControlsRef.current.target.x,
            y: orbitControlsRef.current.target.y,
            z: orbitControlsRef.current.target.z,
          },
          fov: fov,
          timestamp: new Date().toISOString(),
        }
        console.log('Camera View Parameters:', cameraData)
        console.log('Copy this for initial params:', JSON.stringify(cameraData))
        return
      }
      
      if (['w', 'a', 's', 'd'].includes(key)) {
        keysPressed.current.add(key)
        event.preventDefault()
      }
    }
    
    const handleKeyUp = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase()
      if (['w', 'a', 's', 'd'].includes(key)) {
        keysPressed.current.delete(key)
        event.preventDefault()
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [camera, orbitControlsRef])
  
  useFrame((_, delta) => {
    if (keysPressed.current.size === 0 || !orbitControlsRef.current) return
    
    // Movement speed (units per second)
    const baseSpeed = 10
    const speed = baseSpeed * delta
    
    // Get camera's forward direction (projected to XZ plane for horizontal movement)
    const forward = new THREE.Vector3()
    camera.getWorldDirection(forward)
    forward.y = 0
    forward.normalize()
    
    // Get right direction (perpendicular to forward)
    const right = new THREE.Vector3()
    right.crossVectors(forward, new THREE.Vector3(0, 1, 0))
    right.normalize()
    
    // Calculate movement delta
    const moveDelta = new THREE.Vector3()
    
    if (keysPressed.current.has('w')) {
      moveDelta.add(forward.clone().multiplyScalar(speed))
    }
    if (keysPressed.current.has('s')) {
      moveDelta.add(forward.clone().multiplyScalar(-speed))
    }
    if (keysPressed.current.has('a')) {
      moveDelta.add(right.clone().multiplyScalar(-speed))
    }
    if (keysPressed.current.has('d')) {
      moveDelta.add(right.clone().multiplyScalar(speed))
    }
    
    // Apply movement to both camera and OrbitControls target
    if (moveDelta.lengthSq() > 0) {
      camera.position.add(moveDelta)
      orbitControlsRef.current.target.add(moveDelta)
    }
  })
  
  return null
}

interface SceneProps {
  cameraTarget: [number, number, number]
}

function Scene({ cameraTarget }: SceneProps) {
  const theme = useUIStore(state => state.theme)
  const currentManifoldId = useSceneStore(state => state.currentManifoldId)
  const manifoldParams = useSceneStore(state => state.manifoldParams)
  const manifoldRange = useSceneStore(state => state.manifoldRange)
  const startX = useSceneStore(state => state.startX)
  const startY = useSceneStore(state => state.startY)
  const trajectories = useSceneStore(state => state.trajectories)
  const currentStep = useAnimationStore(state => state.currentStep)
  const showTrails = useAnimationStore(state => state.showTrails)
  const showManifoldMesh = useAnimationStore(state => state.showManifoldMesh)
  
  const landscapeMeshRef = useRef<THREE.Mesh>(null)
  const orbitControlsRef = useRef<any>(null)
  
  // Calculate effective height scale based on vertical_scale parameter
  const effectiveHeightScale = useMemo(() => {
    const verticalScale = manifoldParams.vertical_scale ?? 1.0
    return HEIGHT_SCALE * verticalScale
  }, [manifoldParams.vertical_scale])
  
  const landscapeData = useMemo(() => {
    return generateManifoldLandscape(
      currentManifoldId,
      80,
      manifoldRange,
      manifoldRange,
      manifoldParams
    )
  }, [currentManifoldId, manifoldParams, manifoldRange])
  
  const bgColor = theme === 'dark' ? '#0a0a0a' : '#fafafa'
  const gridColor1 = theme === 'dark' ? '#444444' : '#cccccc'
  const gridColor2 = theme === 'dark' ? '#222222' : '#e8e8e8'
  
  return (
    <>
      <color attach="background" args={[bgColor]} />
      
      {/* Lighting */}
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 10, 5]} intensity={0.8} />
      <directionalLight position={[-10, -10, -5]} intensity={0.4} />
      
      {/* Grid */}
      <gridHelper args={[20, 20, gridColor1, gridColor2]} />
      
      {/* Landscape */}
      <LandscapeMesh data={landscapeData} meshRef={landscapeMeshRef} showWireframe={showManifoldMesh} heightScale={effectiveHeightScale} />
      
      {/* Start point marker */}
      <StartPointMarker x={startX} y={startY} landscapeData={landscapeData} heightScale={effectiveHeightScale} />
      
      {/* Trajectory lines */}
      <TrajectoryLines 
        trajectories={trajectories} 
        landscapeData={landscapeData}
        currentStep={currentStep}
        showTrails={showTrails}
        heightScale={effectiveHeightScale}
      />
      
      {/* Optimizer balls */}
      <OptimizerBalls 
        trajectories={trajectories}
        landscapeData={landscapeData}
        currentStep={currentStep}
        heightScale={effectiveHeightScale}
      />
      
      {/* Camera controls */}
      <OrbitControls 
        ref={orbitControlsRef}
        target={cameraTarget}
        enableDamping 
        dampingFactor={0.05}
        minDistance={5}
        maxDistance={50}
      />
      
      {/* Keyboard controls for WASD movement */}
      <KeyboardControls orbitControlsRef={orbitControlsRef} />
      
      {/* Animation controller */}
      <AnimationController />
    </>
  )
}

// Camera configurations
const DESKTOP_CAMERA = {
  position: [-4.10, 7.54, -8.44] as [number, number, number],
  target: [0, 0, 0] as [number, number, number],
  fov: 60,
}

const MOBILE_CAMERA = {
  position: [-16.78683426835478, 18.85000740374781, -16.18529407693264] as [number, number, number],
  target: [0.3344133140346723, 0, 0.24277936987569657] as [number, number, number],
  fov: 60,
}

interface Canvas3DProps {
  isMobile: boolean
}

export function Canvas3D({ isMobile }: Canvas3DProps) {
  const pickingMode = useUIStore(state => state.pickingMode)
  
  const cameraConfig = isMobile ? MOBILE_CAMERA : DESKTOP_CAMERA
  
  return (
    <div 
      className={`${styles.canvasContainer} ${pickingMode ? styles.pickingMode : ''}`}
    >
      <Canvas
        camera={{ position: cameraConfig.position, fov: cameraConfig.fov }}
        gl={{ antialias: true }}
      >
        <Scene cameraTarget={cameraConfig.target} />
      </Canvas>
    </div>
  )
}
